import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, ChevronRight, Loader2, Building2, CalendarClock, ExternalLink, ShoppingBag, CreditCard, MapPin, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../components/CurrencySelector';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGES = {
  pending:  { color: 'bg-amber-100 text-amber-800 border-amber-200',   labelFr: 'En attente',   labelEn: 'Pending' },
  sent:     { color: 'bg-sky-100 text-sky-800 border-sky-200',         labelFr: 'Devis reçu',   labelEn: 'Quote received' },
  accepted: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', labelFr: 'Accepté',  labelEn: 'Accepted' },
  rejected: { color: 'bg-rose-100 text-rose-800 border-rose-200',      labelFr: 'Décliné',      labelEn: 'Declined' },
};

export const MyQuotesPage = () => {
  const { token, isAuthenticated } = useAuth();
  const { language } = useLanguage();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [checkoutQuote, setCheckoutQuote] = useState(null);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [addr, setAddr] = useState({
    first_name: '', last_name: '', address_line1: '', address_line2: '',
    city: '', postal_code: '', country: 'FR', phone: '',
  });
  const [shippingMethod, setShippingMethod] = useState('standard');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/quotes/me`, { headers: { Authorization: `Bearer ${token}` } });
        setQuotes(data || []);
      } catch (err) {
        toast.error(T('Impossible de charger les devis', 'Failed to load quotes'));
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated, token, navigate]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  const openCheckout = (q) => {
    setSelectedQuote(null);
    setCheckoutQuote(q);
    // Pre-fill with anything we know about the buyer
    setAddr((a) => ({ ...a, first_name: a.first_name, last_name: a.last_name }));
  };

  const submitCheckout = async () => {
    if (!checkoutQuote) return;
    // Basic validation
    const required = ['first_name', 'last_name', 'address_line1', 'city', 'postal_code', 'country'];
    for (const f of required) {
      if (!addr[f] || !String(addr[f]).trim()) {
        toast.error(T('Veuillez remplir tous les champs obligatoires', 'Please fill all required fields'));
        return;
      }
    }
    setCheckoutSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/quotes/${checkoutQuote.id}/checkout`,
        {
          origin_url: window.location.origin,
          shipping_address: addr,
          shipping_method: shippingMethod,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data?.url) {
        toast.success(T('Redirection vers le paiement sécurisé...', 'Redirecting to secure payment...'));
        window.location.href = data.url;
        return;
      }
      toast.error(T('Erreur de paiement', 'Payment error'));
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur lors du paiement', 'Checkout failed'));
    } finally {
      setCheckoutSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="my-quotes-page">
      <div className="bg-gradient-to-br from-[#0F4C5C] to-[#0B3A47] text-white py-10">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-3">
            <Link to="/" className="hover:text-white">{T('Accueil', 'Home')}</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-[#E9C46A]">{T('Mes devis', 'My quotes')}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <FileText className="w-8 h-8 text-[#E9C46A]" />
            {T('Mes devis B2B', 'My B2B quotes')}
          </h1>
          <p className="text-white/70 mt-2">
            {T('Suivez vos demandes de devis et les réponses des vendeurs.', 'Track your quote requests and vendor responses.')}
          </p>
        </div>
      </div>

      <div className="container-main py-10">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C5C]" /></div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#0F4C5C]/10">
            <FileText className="w-14 h-14 mx-auto text-[#0F4C5C]/30 mb-4" />
            <h2 className="text-xl font-bold text-[#0F4C5C] mb-2">
              {T('Aucun devis pour le moment', 'No quotes yet')}
            </h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              {T('Vous pouvez demander un devis personnalisé sur chaque fiche produit en bas de page, en cliquant sur "Demander un devis".', 'You can request a custom quote on each product page by clicking "Request a quote".')}
            </p>
            <Button onClick={() => navigate('/shop')} className="btn-primary">
              <ShoppingBag className="w-4 h-4 mr-2" />
              {T('Parcourir le catalogue', 'Browse catalog')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {quotes.map((q) => {
              const badge = STATUS_BADGES[q.status] || STATUS_BADGES.pending;
              return (
                <div
                  key={q.id}
                  data-testid={`my-quote-${q.id}`}
                  className="bg-white border border-[#0F4C5C]/10 rounded-xl p-5 hover:border-[#0F4C5C]/30 transition-colors cursor-pointer"
                  onClick={() => setSelectedQuote(q)}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <h3 className="font-bold text-[#0F4C5C]">
                          {T('Devis pour', 'Quote for')} {q.company_name}
                        </h3>
                        <Badge className={`${badge.color} border`}>{T(badge.labelFr, badge.labelEn)}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-2">
                        <CalendarClock className="w-3 h-3" />
                        {T('Demande envoyée le', 'Request sent on')} {formatDate(q.created_at)}
                      </p>
                      <p className="text-sm text-[#0F4C5C]/80 flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5" />
                        {q.items?.length || 0} {T('produits', 'products')} · {q.vendor_ids?.length || 0} {T('vendeur(s)', 'vendor(s)')}
                      </p>
                    </div>
                    <div className="text-right">
                      {q.quoted_total ? (
                        <>
                          <p className="text-xs text-muted-foreground">{T('Devis vendeur', 'Vendor quote')}</p>
                          <p className="text-2xl font-bold text-[#0F4C5C]">{format(q.quoted_total)}</p>
                          {q.valid_until && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {T('Valable jusqu\'au', 'Valid until')} {formatDate(q.valid_until)}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">{T('En attente de réponse', 'Awaiting response')}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!selectedQuote} onOpenChange={(open) => { if (!open) setSelectedQuote(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedQuote && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#0F4C5C]" />
                  {T('Détails du devis', 'Quote details')}
                </DialogTitle>
                <DialogDescription>
                  {selectedQuote.company_name} · {formatDate(selectedQuote.created_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-bold text-[#0F4C5C] mb-2">{T('Produits demandés', 'Requested products')}</h4>
                  <div className="space-y-2">
                    {(selectedQuote.items || []).map((it) => {
                      const unit = selectedQuote.quoted_unit_prices?.[it.product_id];
                      return (
                        <div key={it.product_id} className="flex items-center justify-between bg-[#FBF8F3] rounded p-3">
                          <div>
                            <p className="text-sm text-[#0F4C5C] font-medium">{it.name}</p>
                            <p className="text-xs text-muted-foreground">{T('Quantité', 'Quantity')}: {it.quantity}</p>
                          </div>
                          <div className="text-right text-sm">
                            <p className="text-muted-foreground line-through text-xs">{format(it.list_price || 0)}</p>
                            {unit ? (
                              <p className="text-emerald-700 font-bold">€{unit.toFixed(2)}</p>
                            ) : (
                              <p className="text-amber-600 italic text-xs">{T('en attente', 'pending')}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {selectedQuote.quoted_total && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm font-semibold text-emerald-900">{T('Total proposé HT', 'Proposed total')}</span>
                      <span className="text-2xl font-bold text-emerald-700">€{selectedQuote.quoted_total.toFixed(2)}</span>
                    </div>
                    {selectedQuote.valid_until && (
                      <p className="text-xs text-emerald-700 mt-2">
                        {T('Offre valable jusqu\'au', 'Offer valid until')} {formatDate(selectedQuote.valid_until)}
                      </p>
                    )}
                  </div>
                )}

                {selectedQuote.vendor_notes && (
                  <div className="bg-sky-50 border-l-4 border-sky-400 p-3 rounded">
                    <p className="text-xs font-semibold text-sky-900 uppercase tracking-wide mb-1">
                      {T('Message du vendeur', 'Vendor message')}
                    </p>
                    <p className="text-sm text-sky-900">{selectedQuote.vendor_notes}</p>
                  </div>
                )}

                {selectedQuote.additional_notes && (
                  <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
                    <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-1">
                      {T('Vos notes', 'Your notes')}
                    </p>
                    <p className="text-sm text-amber-900">{selectedQuote.additional_notes}</p>
                  </div>
                )}

                <div className="flex gap-2 justify-end pt-2 border-t">
                  <Button variant="outline" onClick={() => setSelectedQuote(null)}>
                    {T('Fermer', 'Close')}
                  </Button>
                  {(selectedQuote.status === 'sent' || selectedQuote.status === 'accepted') && !selectedQuote.converted_order_id && (
                    <Button
                      className="btn-primary"
                      onClick={() => openCheckout(selectedQuote)}
                      data-testid="quote-checkout-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-1.5" />
                      {T('Accepter et payer', 'Accept and pay')}
                    </Button>
                  )}
                  {selectedQuote.converted_order_id && (
                    <Button variant="outline" disabled>
                      <ExternalLink className="w-4 h-4 mr-1.5" />
                      {T('Déjà converti en commande', 'Already converted to order')}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout dialog: convert quote to Stripe session */}
      <Dialog open={!!checkoutQuote} onOpenChange={(open) => { if (!open && !checkoutSubmitting) setCheckoutQuote(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          {checkoutQuote && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#0F4C5C]" />
                  {T('Finaliser le devis', 'Finalize quote')}
                </DialogTitle>
                <DialogDescription>
                  {T(`Vous êtes sur le point d'accepter le devis de`, `You are about to accept the quote from`)} <strong>{checkoutQuote.company_name}</strong> {T(`pour`, `for`)} <strong>{format(checkoutQuote.quoted_total || 0)}</strong>.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-xs font-bold text-emerald-900 uppercase mb-1">{T('Montant final HT', 'Final amount')}</p>
                  <p className="text-2xl font-bold text-emerald-700">{format(checkoutQuote.quoted_total || 0)}</p>
                  <p className="text-xs text-emerald-700 mt-1">
                    {T('+ frais de port calculés à l\'étape suivante', '+ shipping calculated on next step')}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-[#0F4C5C] flex items-center gap-1.5 mb-3">
                    <MapPin className="w-4 h-4" />
                    {T('Adresse de livraison', 'Shipping address')}
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="qco-fname">{T('Prénom', 'First name')} *</Label>
                      <Input id="qco-fname" data-testid="qco-fname" value={addr.first_name} onChange={(e) => setAddr({ ...addr, first_name: e.target.value })} className="mt-1.5 h-10" />
                    </div>
                    <div>
                      <Label htmlFor="qco-lname">{T('Nom', 'Last name')} *</Label>
                      <Input id="qco-lname" data-testid="qco-lname" value={addr.last_name} onChange={(e) => setAddr({ ...addr, last_name: e.target.value })} className="mt-1.5 h-10" />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="qco-addr1">{T('Adresse', 'Address')} *</Label>
                      <Input id="qco-addr1" data-testid="qco-addr1" value={addr.address_line1} onChange={(e) => setAddr({ ...addr, address_line1: e.target.value })} className="mt-1.5 h-10" />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="qco-addr2">{T('Complément (optionnel)', 'Address line 2')}</Label>
                      <Input id="qco-addr2" value={addr.address_line2} onChange={(e) => setAddr({ ...addr, address_line2: e.target.value })} className="mt-1.5 h-10" />
                    </div>
                    <div>
                      <Label htmlFor="qco-zip">{T('Code postal', 'Postal code')} *</Label>
                      <Input id="qco-zip" data-testid="qco-zip" value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} className="mt-1.5 h-10" />
                    </div>
                    <div>
                      <Label htmlFor="qco-city">{T('Ville', 'City')} *</Label>
                      <Input id="qco-city" data-testid="qco-city" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className="mt-1.5 h-10" />
                    </div>
                    <div>
                      <Label htmlFor="qco-country">{T('Pays', 'Country')} *</Label>
                      <Select value={addr.country} onValueChange={(v) => setAddr({ ...addr, country: v })}>
                        <SelectTrigger id="qco-country" data-testid="qco-country" className="mt-1.5 h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="FR">🇫🇷 France</SelectItem>
                          <SelectItem value="BE">🇧🇪 Belgique</SelectItem>
                          <SelectItem value="CH">🇨🇭 Suisse</SelectItem>
                          <SelectItem value="LU">🇱🇺 Luxembourg</SelectItem>
                          <SelectItem value="DE">🇩🇪 Allemagne</SelectItem>
                          <SelectItem value="IT">🇮🇹 Italie</SelectItem>
                          <SelectItem value="ES">🇪🇸 Espagne</SelectItem>
                          <SelectItem value="NL">🇳🇱 Pays-Bas</SelectItem>
                          <SelectItem value="PT">🇵🇹 Portugal</SelectItem>
                          <SelectItem value="GB">🇬🇧 Royaume-Uni</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="qco-phone">{T('Téléphone', 'Phone')}</Label>
                      <Input id="qco-phone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} className="mt-1.5 h-10" />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="qco-shipmethod">{T('Mode de livraison', 'Shipping method')}</Label>
                  <Select value={shippingMethod} onValueChange={setShippingMethod}>
                    <SelectTrigger id="qco-shipmethod" className="mt-1.5 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">{T('Standard (3-5 jours)', 'Standard (3-5 days)')}</SelectItem>
                      <SelectItem value="express">{T('Express (1-2 jours)', 'Express (1-2 days)')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-[#FBF8F3] rounded p-3">
                  <Lock className="w-3.5 h-3.5 text-[#2A9D8F]" />
                  {T('Paiement sécurisé par Stripe. Aucune donnée bancaire stockée sur notre serveur.', 'Secure payment by Stripe. No card data stored on our server.')}
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setCheckoutQuote(null)} disabled={checkoutSubmitting}>
                  {T('Annuler', 'Cancel')}
                </Button>
                <Button
                  className="btn-primary"
                  onClick={submitCheckout}
                  disabled={checkoutSubmitting}
                  data-testid="quote-pay-now-btn"
                >
                  {checkoutSubmitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1.5" />}
                  {T('Payer maintenant', 'Pay now')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyQuotesPage;
